import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { findNationalities } from '~/Api/AePriceReferencing/MaxPrice';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	AeMaxPriceResult,
	createExportXls,
	CreateExportXlsResult,
	ErrorResponse,
	findAeMaxPrice,
	ProductUpdate,
	ReferenceUpdate,
	updateAeMaxPrice as apiUpdateAeMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/AeMaxPrice';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';

const updateQueue = new PromiseQueue(1, Infinity);

export function fetchAeMaxPrice(groupId: string): ThunkAction<Promise<AeMaxPriceResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findAeMaxPrice, userId, groupId));
		dispatch({ type: ActionType.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionType.receive,
				groupId,
				request,
				aeMaxPrice: response?.aeMaxPrice || null,
			})
		);
	};
}

export function updateAeMaxPrice(
	groupId: string,
	reference: ReferenceUpdate,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		const request = updateQueue.add(() => dispatch(immediatelyUpdateAeMaxPrice(groupId, reference, products)));
		dispatch({ type: ActionType.requestUpdate, groupId, request });
		return request;
	};
}

function immediatelyUpdateAeMaxPrice(
	groupId: string,
	reference: ReferenceUpdate,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(invokeApi(apiUpdateAeMaxPrice, userId, groupId, reference, products))
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				type: ActionType.receiveUpdate,
				groupId,
				request,
				aeMaxPrice: response?.aeMaxPrice || null,
			})
		);
	};
}

export function handleAeMaxPriceUpdateResponse(response: any, groupId: string): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}

		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchAeMaxPrice(groupId)).catch((response) => handleError(response));
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchAeMaxPrice(groupId)).catch((response) => handleError(response));
		}
		dispatch(handleError(response));
	};
}

export function createAeMaxPriceExportXls(groupId: string): ThunkAction<Promise<CreateExportXlsResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(createExportXls, userId, groupId));
		dispatch({ type: ActionType.requestXlsCreate, groupId, request });

		return resolve(request, (response, error) =>
			dispatch({
				type: ActionType.receiveXlsCreate,
				request,
				validationResult: error
					? error instanceof ErrorResponse
						? error.toValidationResult()
						: null
					: new ValidationResult(new Set(), [], []),
			})
		);
	};
}

export function pruneAeMaxPrice(groupId: string) {
	return { type: ActionType.prune, groupId };
}

export function scheduleAeMaxPriceUpdate(
	groupId: string,
	reference: ReferenceUpdate,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		return new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateAeMaxPrice(groupId, reference, products)));
			}, 2000);

			dispatch({
				type: ActionType.scheduleUpdate,
				timeoutId,
				groupId,
				reference,
				products,
			});
		});
	};
}

export function commitAeMaxPriceUpdate(groupId: string): ThunkAction<Promise<void>, Action> {
	return async (dispatch, getState) => {
		const { update } = getState().aeMaxPrice;
		if (!update || update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, reference, products } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateAeMaxPrice(groupId, reference, products));
		}
	};
}

export function handleCommitError(error: any): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
}

export function fetchNationalities(): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const request = dispatch(invokeApi(findNationalities));
		dispatch({ type: ActionType.requestNationalities, request });

		await resolve(request, (response, error) => {
			dispatch({
				type: ActionType.receiveNationalities,
				request,
				items: response?.items || null,
			});
			if (error) {
				handleError(error);
			}
		});
	};
}

export function loadNationalities(): ThunkAction<Promise<void>, Action> {
	return async (dispatch, getState) => {
		await (getState().aeMaxPrice.nationalities?.request || dispatch(fetchNationalities()));
	};
}
